import { StatsigClient } from '@statsig/js-client'

import { STATSIG_ENVIRONMENT_TIER } from './statsigConfig'

let statsigClient: StatsigClient | null = null
let isInitialized = false

export interface StatsigClientInitOptions {
    clientKey: string
    stableID: string
    bootstrapValues: string | null
}

/**
 * Initialize Statsig client on the browser.
 * Uses bootstrap values from server to avoid making an API call.
 */
export const initializeStatsigClient = async (
    options: StatsigClientInitOptions
): Promise<StatsigClient | null> => {
    // Only run on client
    if (typeof window === 'undefined') {
        return null
    }

    // Don't reinitialize if already done
    if (isInitialized && statsigClient) {
        return statsigClient
    }

    const { clientKey, stableID, bootstrapValues } = options

    if (!clientKey) {
        console.warn('[Statsig] Client key not provided')
        return null
    }

    try {
        const user = {
            customIDs: { stableID }
        }

        statsigClient = new StatsigClient(clientKey, user, {
            environment: { tier: STATSIG_ENVIRONMENT_TIER }
        })

        if (bootstrapValues) {
            // Bootstrap with server values (instant, no network call)
            statsigClient.dataAdapter.setData(bootstrapValues)
            statsigClient.initializeSync()
            isInitialized = true
            console.log('[Statsig] Client initialized with bootstrap values (sync)')
        } else {
            // No bootstrap values - fetch from network
            await statsigClient.initializeAsync()
            isInitialized = true
            console.log('[Statsig] Client initialized via network')
        }

        // Alias for backward compatibility with legacy raven/global Statsig usage
        ;(window as any).Statsig = statsigClient

        return statsigClient
    } catch (error) {
        console.error('[Statsig] Failed to initialize client:', error)
        return null
    }
}

/**
 * Get the Statsig client instance.
 * Returns null if not initialized.
 */
export const getStatsigClient = (): StatsigClient | null => {
    return statsigClient
}

/**
 * Check if Statsig client is initialized.
 */
export const isStatsigClientInitialized = (): boolean => {
    return isInitialized
}

/**
 * Get an experiment value on the client side.
 *
 * @example
 * const variant = getClientExperiment({
 *   experimentName: 'seat_reco_dom',
 *   paramName: 'variant',
 *   fallback: 'control'
 * });
 */
export const getClientExperiment = <T extends string = string>(options: {
    experimentName: string
    paramName: string
    fallback: T
}): T => {
    const { experimentName, paramName, fallback } = options

    if (!statsigClient || !isInitialized) {
        console.warn(
            `[Statsig] Client not initialized, returning fallback for: ${experimentName}`
        )
        return fallback
    }

    try {
        const experiment = statsigClient.getExperiment(experimentName)
        const value = experiment.get(paramName, fallback)
        return value as T
    } catch (error) {
        console.error(`[Statsig] Error getting experiment ${experimentName}:`, error)
        return fallback
    }
}

/**
 * Check a feature gate on the client side.
 */
export const checkClientGate = (gateName: string): boolean => {
    if (!statsigClient || !isInitialized) {
        console.warn(
            `[Statsig] Client not initialized, returning false for: ${gateName}`
        )
        return false
    }

    try {
        return statsigClient.checkGate(gateName)
    } catch (error) {
        console.error(`[Statsig] Error checking gate ${gateName}:`, error)
        return false
    }
}
